import { useState, useEffect } from 'react'
import Button from '../../../../components/common/Button'
import FileValidationPanel from './FileValidationPanel'
import SimilarScenariosWarning from './SimilarScenariosWarning'

/**
 * Step 2: Confirm Metadata
 * User reviews and confirms extracted metadata from Synchro CSV
 * @param {Object} step - Step object with output_data.metadata
 * @param {Function} onSubmit - Callback to submit user input
 */
function MetadataConfirmation({ step, onSubmit, loading = false }) {
  // Debug logging
  console.log('MetadataConfirmation step:', step)
  console.log('step.output_data:', step.output_data)
  console.log('step.output_data.metadata:', step.output_data?.metadata)

  const metadata = step.output_data?.metadata || {}
  const [confirmedMetadata] = useState(metadata)

  const handleSubmit = () => {
    // Mark all items as confirmed, using updated intersections from state
    const confirmedData = {
      intersections: intersections.map((i) => ({
        ...(typeof i === 'object' ? i : { name: i }),
        confirmed: true,
      })),
      scenarios: (confirmedMetadata.scenarios || []).map((s) => ({
        ...(typeof s === 'object' ? s : { name: s }),
        confirmed: true,
      })),
      analysis_types: (confirmedMetadata.analysis_types || []).map((at) => ({
        ...(typeof at === 'object' ? at : { name: at }),
        confirmed: true,
      })),
      time_periods: (confirmedMetadata.time_periods || []).map((tp) => ({
        ...(typeof tp === 'object' ? tp : { name: tp }),
        confirmed: true,
      })),
    }

    onSubmit({
      metadata: confirmedData,
      // FEATURE-011: Include scenario merge selections
      scenario_merges: scenarioMerges,
    })
  }

  // Backend returns objects with {name, node_number, confirmed} for intersections
  // and {name, confirmed} for other metadata
  const [intersections, setIntersections] = useState(confirmedMetadata.intersections || [])
  const [editingNodeIndex, setEditingNodeIndex] = useState(null)

  const time_periods = (confirmedMetadata.time_periods || []).map((tp) =>
    typeof tp === 'string' ? tp : tp.name
  )
  const scenarios = (confirmedMetadata.scenarios || []).map((s) =>
    typeof s === 'string' ? s : s.name
  )
  const analysis_types = (confirmedMetadata.analysis_types || []).map((at) =>
    typeof at === 'string' ? at : at.name
  )

  const handleNodeNumberClick = (index) => {
    setEditingNodeIndex(index)
  }

  const handleNodeNumberChange = (index, newValue) => {
    // Only allow numeric input
    if (newValue === '' || /^\d+$/.test(newValue)) {
      const updated = [...intersections]
      updated[index] = {
        ...updated[index],
        node_number: newValue,
      }
      setIntersections(updated)
    }
  }

  const handleNodeNumberBlur = () => {
    setEditingNodeIndex(null)
  }

  const handleNodeNumberKeyDown = (e) => {
    if (e.key === 'Enter') {
      setEditingNodeIndex(null)
    } else if (e.key === 'Escape') {
      setEditingNodeIndex(null)
    }
  }

  // BUG-016: Get excluded intersections info
  const excludedIntersections = metadata.excluded_intersections || { count: 0 }

  // FEATURE-011: Get similar scenarios info and manage merge selections
  const similarScenarios = metadata.similar_scenarios || { has_similar: false, groups: [] }
  const [scenarioMerges, setScenarioMerges] = useState([])

  // Initialize scenario merge selections from metadata
  useEffect(() => {
    if (similarScenarios?.groups?.length > 0) {
      setScenarioMerges(
        similarScenarios.groups.map((group) => ({
          normalized: group.normalized,
          merge: true, // Default to merge (most common case)
          canonical_name: group.canonical,
          variants: group.variants.map((v) => v.name),
        }))
      )
    }
    // Only re-init when group count changes, not on every groups array reference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [similarScenarios?.groups?.length])

  return (
    <div className="space-y-6">
      {/* BUG-016: Info banner for excluded intersections (show at top for visibility) */}
      {excludedIntersections.count > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-500"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="text-sm text-blue-800">
              <p className="font-medium">
                {excludedIntersections.count} intersection
                {excludedIntersections.count !== 1 ? 's' : ''} will be excluded from output
              </p>
              {excludedIntersections.files && excludedIntersections.files.length > 0 && (
                <p className="mt-1 text-blue-700">
                  <span className="font-medium">Found in:</span>{' '}
                  {excludedIntersections.files.join(', ')}
                </p>
              )}
              <p className="mt-1 text-blue-700">
                Sections without an intersection name are omitted from the final report. If this is
                unintended, cancel and update the source files.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE-011: Similar scenarios warning */}
      {similarScenarios.has_similar && (
        <SimilarScenariosWarning
          similarScenarios={similarScenarios}
          mergeSelections={scenarioMerges}
          onMergeChange={setScenarioMerges}
        />
      )}

      {/* FEATURE-009: File validation panel */}
      {metadata.file_results && <FileValidationPanel fileResults={metadata.file_results} />}

      {/* Existing: Metadata confirmation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Extracted Metadata</h3>

        <p className="text-sm text-gray-600 mb-6">
          Please review the metadata extracted from your CSV file. You can confirm or edit as
          needed.
        </p>

        <div className="space-y-6">
          {/* Intersections */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intersections Found ({intersections.length})
            </label>
            {intersections.length > 0 ? (
              <ul className="space-y-1 bg-gray-50 rounded p-3 max-h-40 overflow-y-auto">
                {intersections.map((intersection, index) => {
                  const name = typeof intersection === 'string' ? intersection : intersection.name
                  const nodeNumber =
                    typeof intersection === 'object' ? intersection.node_number : null
                  const isEditing = editingNodeIndex === index
                  return (
                    <li key={index} className="text-sm text-gray-700 flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      <span className="flex-1">{name}</span>
                      {nodeNumber && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-600">Node</span>
                          {isEditing ? (
                            <input
                              type="text"
                              value={nodeNumber}
                              onChange={(e) => handleNodeNumberChange(index, e.target.value)}
                              onBlur={handleNodeNumberBlur}
                              onKeyDown={handleNodeNumberKeyDown}
                              className="w-12 text-xs font-mono bg-white border border-blue-300 px-2 py-0.5 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                          ) : (
                            <button
                              onClick={() => handleNodeNumberClick(index)}
                              className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded hover:bg-gray-200 hover:text-gray-700 transition-colors cursor-pointer"
                              title="Click to edit node number"
                            >
                              {nodeNumber}
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="text-sm text-amber-600">No intersections found</p>
            )}
          </div>

          {/* Time Periods */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Periods ({time_periods.length})
            </label>
            {time_periods.length > 0 ? (
              <ul className="space-y-1 bg-gray-50 rounded p-3">
                {time_periods.map((period, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    {period}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-amber-600">No time periods found</p>
            )}
          </div>

          {/* Scenarios */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scenarios ({scenarios.length})
            </label>
            {scenarios.length > 0 ? (
              <ul className="space-y-1 bg-gray-50 rounded p-3">
                {scenarios.map((scenario, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    {scenario}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-amber-600">No scenarios found</p>
            )}
          </div>

          {/* Analysis Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Analysis Types ({analysis_types.length})
            </label>
            {analysis_types.length > 0 ? (
              <ul className="space-y-1 bg-gray-50 rounded p-3">
                {analysis_types.map((type, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    {type}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-amber-600">No analysis types found</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Submitting...' : 'Confirm & Continue'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default MetadataConfirmation
